"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
exports.__esModule = true;
exports.getTransactionStatus = void 0;
var errors_1 = require("@ledgerhq/errors");
var errors_2 = require("@ledgerhq/errors");
var eip55_1 = __importDefault(require("eip55"));
var invariant_1 = __importDefault(require("invariant"));
var bignumber_js_1 = __importDefault(require("bignumber.js"));
var transaction_1 = require("./transaction");
var logic_1 = require("./logic");
var env_1 = require("../../env");
var modules_1 = require("./modules");
var isRecipientValid = function (currency, recipient, isFullLower, isFullUpper) {
    if (!(0, logic_1.isEthereumAddress)(recipient))
        return false;
    // To handle non-eip55 addresses we stop validation here if we detect
    // address is either full upper or full lower.
    // see https://github.com/LedgerHQ/ledger-live-desktop/issues/1397
    if (isFullUpper || isFullLower)
        return true;
    try {
        return eip55_1["default"].verify(recipient);
    }
    catch (error) {
        return false;
    }
};
var validateRecipient = function (currency, recipient, _a) {
    var errors = _a.errors, warnings = _a.warnings;
    var slice = recipient.substr(2);
    var isFullLower = slice === slice.toLowerCase();
    var isFullUpper = slice === slice.toUpperCase();
    if (!recipient) {
        errors.recipient = new errors_1.RecipientRequired("");
    }
    else if (!isRecipientValid(currency, recipient, isFullLower, isFullUpper)) {
        errors.recipient = new errors_1.InvalidAddress("", {
            currencyName: currency.name
        });
    }
    else if (isFullUpper || isFullLower) {
        // Address is not respecting the EIP-55 checksum
        warnings.recipient = new errors_1.ETHAddressNonEIP();
    }
};
var validateLegacyGas = function (currency, tx, _a) {
    var errors = _a.errors;
    if (!tx.gasPrice) {
        errors.gasPrice = new errors_2.FeeNotLoaded();
    }
};
var validateEIP1559Gas = function (currency, tx, _a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    var errors = _a.errors, warnings = _a.warnings;
    // Minimal estimation is next base fee + the minimal priority fee
    var networkMinimumGasEstimation = (_c = (_b = tx.networkInfo) === null || _b === void 0 ? void 0 : _b.nextBaseFeePerGas) === null || _c === void 0 ? void 0 : _c.plus(((_e = (_d = tx.networkInfo) === null || _d === void 0 ? void 0 : _d.maxPriorityFeePerGas) === null || _e === void 0 ? void 0 : _e.min) || 0);
    if (!tx.maxFeePerGas) {
        errors.maxFee = new errors_2.FeeNotLoaded();
    }
    else if (tx.maxFeePerGas.isEqualTo(0)) {
        errors.maxFee = new errors_2.FeeRequired();
    }
    else if (tx.maxFeePerGas.lt(networkMinimumGasEstimation || 0)) {
        warnings.maxFee = new errors_2.MaxFeeTooLow();
    }
    var shouldGateMinimumPriorityFee = (0, env_1.getEnv)("EIP1559_MINIMUM_FEES_GATE");
    var minimunPriorityFee = (_h = (_g = (_f = tx === null || tx === void 0 ? void 0 : tx.networkInfo) === null || _f === void 0 ? void 0 : _f.maxPriorityFeePerGas) === null || _g === void 0 ? void 0 : _g.min) === null || _h === void 0 ? void 0 : _h.times((0, env_1.getEnv)("EIP1559_PRIORITY_FEE_LOWER_GATE"));
    var maximalPriorityFee = (_k = (_j = tx === null || tx === void 0 ? void 0 : tx.networkInfo) === null || _j === void 0 ? void 0 : _j.maxPriorityFeePerGas) === null || _k === void 0 ? void 0 : _k.max;
    if (!tx.maxPriorityFeePerGas) {
        errors.maxPriorityFee = new errors_2.FeeNotLoaded();
    }
    else if (tx.maxPriorityFeePerGas.isEqualTo(0)) {
        errors.maxPriorityFee = new errors_2.FeeRequired();
    }
    else if ((_l = tx.maxPriorityFeePerGas) === null || _l === void 0 ? void 0 : _l.isGreaterThan(tx.maxFeePerGas || 0)) {
        // priority fee is more than max fee (total fee for the transaction) which doesn't make sense
        errors.maxPriorityFee = new errors_2.PriorityFeeHigherThanMaxFee();
    }
    else if (shouldGateMinimumPriorityFee &&
        tx.maxPriorityFeePerGas.lt(minimunPriorityFee || 0)) {
        // Don't send a transaction with a priority fee more than X % (default is 15%) inferior to network conditions
        // With a low enough priority fee, your transaction might get stuck forever since you can't cancel or boost tx in the live
        errors.maxPriorityFee = new errors_2.PriorityFeeTooLow();
    }
    else if (tx.maxPriorityFeePerGas.gt(maximalPriorityFee || 0)) {
        warnings.maxPriorityFee = new errors_2.PriorityFeeTooHigh();
    }
};
var getTransactionStatus = function (account, tx) {
    var gasLimit = (0, transaction_1.getGasLimit)(tx);
    var estimatedGasPrice = (function () {
        if ((0, transaction_1.EIP1559ShouldBeUsed)(account.currency)) {
            return tx.maxFeePerGas || new bignumber_js_1["default"](0);
        }
        return tx.gasPrice;
    })();
    var estimatedFees = (estimatedGasPrice || new bignumber_js_1["default"](0)).times(gasLimit);
    var errors = {};
    var warnings = {};
    var status = {
        errors: errors,
        warnings: warnings,
        estimatedFees: estimatedFees,
        amount: new bignumber_js_1["default"](0),
        totalSpent: new bignumber_js_1["default"](0)
    };
    validateRecipient(account.currency, tx.recipient, status);
    var mode = modules_1.modes[tx.mode];
    (0, invariant_1["default"])(mode, "missing module for mode=" + tx.mode);
    mode.fillTransactionStatus(account, tx, status);
    if ((0, transaction_1.EIP1559ShouldBeUsed)(account.currency)) {
        validateEIP1559Gas(account.currency, tx, status);
    }
    else {
        validateLegacyGas(account.currency, tx, status);
    }
    if (gasLimit.eq(0)) {
        errors.gasLimit = new errors_2.FeeRequired();
    }
    else if (!errors.recipient) {
        if (estimatedFees.gt(account.balance)) {
            errors.gasPrice = new errors_2.NotEnoughGas();
        }
    }
    if (tx.estimatedGasLimit && gasLimit.lt(tx.estimatedGasLimit)) {
        warnings.gasLimit = new errors_2.GasLessThanEstimate();
    }
    return Promise.resolve(status);
};
exports.getTransactionStatus = getTransactionStatus;
exports["default"] = exports.getTransactionStatus;
//# sourceMappingURL=getTransactionStatus.js.map